/**
 * The season-only parts of the Stat Tracker: the record/points strip and the
 * schedule of games the rollup counted (FR-STATTRACKER-02).
 *
 * Kept out of `stat-tracker.tsx` so the page stays a description of the screen.
 * Only active versions feed these numbers — the server excludes conflicted
 * losing versions from the rollup, so nothing needs filtering here.
 */
import type { SeasonRollupFeed } from '../../lib/api-types';

export interface SeasonRecordStripProps {
  feed: SeasonRollupFeed;
}

export function SeasonRecordStrip({ feed }: SeasonRecordStripProps): JSX.Element {
  const { record } = feed;
  return (
    <dl className="stat-tracker__record" data-testid="season-record">
      <div>
        <dt>Record</dt>
        <dd className="num">
          {record.wins}-{record.losses}
          {record.ties > 0 ? `-${record.ties}` : ''}
        </dd>
      </div>
      <div>
        <dt>Points for</dt>
        <dd className="num">{feed.points_for}</dd>
      </div>
      <div>
        <dt>Points against</dt>
        <dd className="num">{feed.points_against}</dd>
      </div>
      <div>
        <dt>Games</dt>
        <dd className="num">{feed.total_events}</dd>
      </div>
    </dl>
  );
}

export interface SeasonGamesTableProps {
  feed: SeasonRollupFeed;
}

export function SeasonGamesTable({ feed }: SeasonGamesTableProps): JSX.Element | null {
  if (feed.games.length === 0) return null;
  return (
    <section className="stat-tracker__games" aria-label="Season schedule">
      <h2>Games counted</h2>
      <div className="stat-tracker__scroller">
        <table className="stat-sheet__table">
          <caption className="visually-hidden">
            Every event whose active play versions fed the {feed.season} rollup
          </caption>
          <thead>
            <tr>
              <th scope="col">Game</th>
              <th scope="col">Home / away</th>
              <th scope="col">Opponent</th>
              <th scope="col" className="stat-sheet__head--num">
                Score
              </th>
              <th scope="col">Result</th>
              <th scope="col" className="stat-sheet__head--num">
                Plays
              </th>
            </tr>
          </thead>
          <tbody>
            {feed.games.map((game) => (
              <tr key={game.event_id} data-testid={`season-game-${game.event_id}`}>
                <th scope="row">{game.name ?? game.opponent_name ?? 'Game'}</th>
                <td>{game.is_home ? 'vs' : '@'}</td>
                <td>{game.opponent_name ?? 'Opponent'}</td>
                <td className="stat-sheet__cell--num num">
                  {game.team_score}–{game.opponent_score}
                </td>
                <td>{game.result === null ? '—' : game.result.toUpperCase()}</td>
                <td className="stat-sheet__cell--num num">{game.play_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
